/**
 * The one registry of audit event tones (TMMAPP-0103, audit A4 §7; P:5575-5590). An action belongs to
 * a family, and a family has a paint: import is info, rule / allocate are the light blue `brand` wash
 * (TMMAPP-0108), grant / revise are warning, reverse / void are error, archive / share are the `grey`
 * wash, and anything else is neutral. Every action has a family (`other` is one), so a
 * new action from a re-vendor never blanks a pill; its adopting unit adds the rule that places it.
 */
import type { PillPaint } from '@/components/Pill';

export type EventFamily =
  | 'import'
  | 'rule'
  | 'allocate'
  | 'grant'
  | 'revise'
  | 'reverse'
  | 'void'
  | 'archive'
  | 'share'
  | 'other';

/** First match wins, so the specific actions sit above the suffix rules. */
const FAMILY_RULES: readonly (readonly [RegExp, EventFamily])[] = [
  [/^IMPORT_/, 'import'],
  [/^(RULE_FIRED|RULE_PARTIAL)$/, 'rule'],
  [
    /^(ALLOCATION_POSTED|CATEGORY_CHANGED|TRANSACTION_POSTED|OCCURRENCE_POSTED|OCCURRENCE_MATCHED|PLAN_LINE_SETTLED)$/,
    'allocate',
  ],
  // TMMAPP-0113: a line classified, paired or linked, a counterparty set on it, a proposal accepted.
  [
    /^(TRANSFER_CLASSIFIED|TRANSFER_PAIRED|REIMBURSEMENT_LINKED|REIMBURSEMENT_CONFIRMED|PAYEE_NAMED_ON_LINE|PAYEE_SUGGESTION_ACCEPTED|PAYEE_SUGGESTION_ACCEPT_ALL|GENESIS_PROPOSAL_ACCEPTED|GENESIS_PROPOSAL_ACCEPT_ALL)$/,
    'allocate',
  ],
  [
    /^(PERMISSION_GRANTED|SUBSCRIPTION_COMPED|AI_WALLET_CREDITED|STATEMENT_PAID|PROMOTION_REDEEMED|REFERRAL_ATTRIBUTED|PENDING_SUBSCRIPTION_COMPLETED)$/,
    'grant',
  ],
  [
    /^(AI_SUSPENSION_SET|STATEMENT_ACTION_REQUIRED|STATEMENT_ATTENTION|STATEMENT_RETRIED)$/,
    'revise',
  ],
  [
    /^(DELETION_REQUESTED_BY_SUPPORT|ACCOUNT_ERASURE_FAILED|SUBSCRIPTION_CREATION_ABANDONED)$/,
    'void',
  ],
  [
    /^(TRANSFER_CLEARED|REIMBURSEMENT_REJECTED|REIMBURSEMENT_UNLINKED|REIMBURSEMENT_WITHDRAWN|PAYEE_ALIAS_REMOVED|PAYEE_CLEARED_ON_LINE|PAYEE_SUGGESTION_REJECTED|PAYEE_CATEGORY_DEFAULT_CLEARED|CONNECTOR_UNMAPPED|CONNECTOR_REMOVED)$/,
    'reverse',
  ],
  [/^(AI_WALLET_FORFEITED)$/, 'void'],
  [/^(CONNECTOR_LINKED|CONNECTOR_MAPPED)$/, 'import'],
  [
    /^(GENESIS_PROPOSAL_DISMISSED|ACCOUNT_INSTITUTION_PROPOSAL_DISMISSED|PAYEE_TEXT_KEPT_UNNAMED)$/,
    'archive',
  ],
  [/^BALANCE_CHECK_DISMISSED$/, 'archive'],
  [/^(BALANCE_CHECK_RECORDED|BALANCE_CHECK_RESOLVED|ACCOUNT_INSTITUTION_CONFIRMED)$/, 'revise'],
  [
    /^(VIRTUAL_ACCOUNTS_REORDERED|ENVELOPES_REORDERED|CATEGORIES_REORDERED|ACCOUNTS_REORDERED|HOLDINGS_REORDERED|PAYEE_MERGED|PAYEE_ALIAS_MOVED|PAYEE_AI_EXCLUSION_SET|PAYEE_CATEGORY_DEFAULT_SET|BUDGET_START_SET|CONNECTOR_UPDATE_MODE)$/,
    'revise',
  ],
  [/^(ALLOCATION_REVERSED|PERMISSION_REVOKED|SHARE_REVOKED)$/, 'reverse'],
  [
    /^(TRANSACTION_VOIDED|RULE_AUTHORITY_LOST|SPLIT_RULE_AUTHORITY_LOST|PLAN_AUTHORITY_LOST|ACCOUNT_LOCKED|USER_FROZEN)$/,
    'void',
  ],
  [/_(DELETED|CANCELLED|FAILED|MISSED|UNMATCHED)$/, 'void'],
  // TMMAPI-0146: an erasure removes contributions on either side; a role change is a share event.
  [/^(CONTRIBUTIONS_REMOVED|VIRTUAL_ACCOUNT_REMOVED_BY_OWNER_ERASURE)$/, 'void'],
  [/^SHARE_/, 'share'],
  [/_(ARCHIVED|UNARCHIVED|RESTORED|CLOSED|DISPOSED)$/, 'archive'],
  // `ALLOCATION_RESPLIT` (TMMAPI-0145): a re-split revises a line's parts.
  [/_(UPDATED|CHANGED|MODIFIED|RENAMED|CONFIRMED|SPLIT|RESPLIT)$/, 'revise'],
];

export function eventFamily(action: string): EventFamily {
  for (const [pattern, family] of FAMILY_RULES) if (pattern.test(action)) return family;
  return 'other';
}

/** A family's pill paint: the table in the header, in one place. */
export const EVENT_PAINT: Readonly<Record<EventFamily, PillPaint>> = {
  import: { tone: 'info' },
  rule: { wash: 'brand' },
  allocate: { wash: 'brand' },
  grant: { tone: 'warning' },
  revise: { tone: 'warning' },
  reverse: { tone: 'error' },
  void: { tone: 'error' },
  archive: { wash: 'grey' },
  share: { wash: 'grey' },
  other: { tone: 'neutral' },
};
